import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { get } from "lodash";
import React, { FC, useMemo } from "react";
import SummaryLine from "src/components/ui-component/form-component/abschluss-page/SummaryLine";
import { useApplication } from "src/context/ApplicationContext";
import { getLocalstorageToken } from "src/context/AuthHelper";
import {
  KOSTEN_ARTEN,
  KOSTEN_POSITION,
  NACHWEIS_POSITION_STATUS,
} from "src/lib/constants/constant";
import MittelabrufSchema from "src/lib/paths/mittelabruf_FULL_Paths.json";
import { DisplayDateTime, DisplayEuroMUI } from "src/utils/formats";
import AbschlussSeiteBox from "../../../../antrag-components/AbschlussSeiteBox";
import {
  belongsToMittelabrufOrganization,
  getLoeweMittelabrufOrganizations,
} from "./LoeweMittelabrufOrganizations";

const MA_P = MittelabrufSchema.attributes;

type OrganizationExpenses = {
  actualTotal: number;
  actualPersonnel: number;
  actualMaterial: number;
  plannedTotal: number;
  plannedPersonnel: number;
  plannedMaterial: number;
};

const toNumber = (value: unknown): number => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const AbschlussMaLoewe: FC = () => {
  const { application } = useApplication();

  const organizations = useMemo(
    () =>
      getLoeweMittelabrufOrganizations(
        application,
        get(getLocalstorageToken(), "organisationId"),
      ),
    [application],
  );

  const expenses = useMemo(() => {
    const positions = get(
      application,
      MA_P.Mittelabruf_Nachweispositions.path,
      [],
    );

    return organizations.map((organization) => {
      const result: OrganizationExpenses = {
        actualTotal: 0,
        actualPersonnel: 0,
        actualMaterial: 0,
        plannedTotal: 0,
        plannedPersonnel: 0,
        plannedMaterial: 0,
      };

      positions.forEach((position: any) => {
        if (
          !belongsToMittelabrufOrganization(
            position,
            organization.id,
            organization.kostentraeger,
          )
        )
          return;
        if (
          get(position, "attributes.Allgemein_Kostenart") !==
          KOSTEN_POSITION.Ausgabe
        )
          return;

        const category = get(
          position,
          "attributes.Allgemein_Kostenart_Finanzierung",
        );
        if (
          category !== KOSTEN_ARTEN.Personalkosten &&
          category !== KOSTEN_ARTEN.Sachkosten
        )
          return;

        const amount = toNumber(
          get(position, "attributes.Allgemein_Ausgaben_Kosten"),
        );
        const isPlanned =
          get(position, "attributes.Allgemein_Status") ===
          NACHWEIS_POSITION_STATUS.geplant;

        if (isPlanned) {
          result.plannedTotal += amount;
          if (category === KOSTEN_ARTEN.Personalkosten)
            result.plannedPersonnel += amount;
          if (category === KOSTEN_ARTEN.Sachkosten)
            result.plannedMaterial += amount;
        } else {
          result.actualTotal += amount;
          if (category === KOSTEN_ARTEN.Personalkosten)
            result.actualPersonnel += amount;
          if (category === KOSTEN_ARTEN.Sachkosten)
            result.actualMaterial += amount;
        }
      });

      return result;
    });
  }, [application, organizations]);

  const totalBy = (key: keyof OrganizationExpenses): number =>
    expenses.reduce((sum, expense) => sum + expense[key], 0);

  return (
    <AbschlussSeiteBox>
      <SummaryLine
        label="Datum der Einreichung"
        value={
          get(application, MA_P.Allgemein_Eingereicht_Am.path)
            ? DisplayDateTime(
                get(application, MA_P.Allgemein_Eingereicht_Am.path),
              )
            : "noch nicht eingereicht"
        }
      />
      <SummaryLine
        label="Projekttitel"
        value={get(
          application,
          MA_P.Foerderantrag.schema.attributes.Allgemein_Projekttitel.path,
          "",
        )}
      />
      <Box sx={{ mb: 2, mt: "1.5rem" }}>
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table
            sx={{
              minWidth: `${Math.max(680, 260 + organizations.length * 140)}px`,
            }}
            size="small"
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: "38%", whiteSpace: "nowrap" }}>
                  Projektpartner
                </TableCell>
                {organizations.map((organization) => (
                  <TableCell
                    key={organization.id}
                    align="right"
                    sx={{ minWidth: 120, whiteSpace: "nowrap" }}
                  >
                    {organization.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>
                  bis jetzt angefallene Projektausgaben
                </TableCell>
                {expenses.map((expense, index) => (
                  <TableCell key={organizations[index].id} align="right">
                    {DisplayEuroMUI(expense.actualTotal)}
                  </TableCell>
                ))}
              </TableRow>
              <TableRow>
                <TableCell sx={{ pl: 4 }}>darunter Personalausgaben</TableCell>
                {expenses.map((expense, index) => (
                  <TableCell key={organizations[index].id} align="right">
                    {DisplayEuroMUI(expense.actualPersonnel)}
                  </TableCell>
                ))}
              </TableRow>
              <TableRow>
                <TableCell sx={{ pl: 4 }}>darunter Sachausgaben</TableCell>
                {expenses.map((expense, index) => (
                  <TableCell key={organizations[index].id} align="right">
                    {DisplayEuroMUI(expense.actualMaterial)}
                  </TableCell>
                ))}
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>
                  in den nächsten 3 Monaten anfallende Projektausgaben
                </TableCell>
                {expenses.map((expense, index) => (
                  <TableCell key={organizations[index].id} align="right">
                    {DisplayEuroMUI(expense.plannedTotal)}
                  </TableCell>
                ))}
              </TableRow>
              <TableRow>
                <TableCell sx={{ pl: 4 }}>darunter Personalausgaben</TableCell>
                {expenses.map((expense, index) => (
                  <TableCell key={organizations[index].id} align="right">
                    {DisplayEuroMUI(expense.plannedPersonnel)}
                  </TableCell>
                ))}
              </TableRow>
              <TableRow>
                <TableCell sx={{ pl: 4 }}>darunter Sachausgaben</TableCell>
                {expenses.map((expense, index) => (
                  <TableCell key={organizations[index].id} align="right">
                    {DisplayEuroMUI(expense.plannedMaterial)}
                  </TableCell>
                ))}
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      <SummaryLine
        label="berechnete max. Förderung / Mittelabruf"
        value={DisplayEuroMUI(
          get(
            application,
            MA_P.Ermittlung_Des_Auszahlungsbetrages_Anforderungsbetrag.path,
            totalBy("actualTotal"),
          ),
        )}
        boldLabel
        showTopDivider
      />
    </AbschlussSeiteBox>
  );
};

export default AbschlussMaLoewe;
